import React from 'react'
import { groupBy } from 'lodash-es'
import { useMemo } from 'react'
import {
  ResponsiveContainer,
  CartesianGrid,
  YAxis,
  Label,
  XAxis,
  Tooltip,
  Line,
  LabelList,
  ComposedChart,
  Bar,
} from 'recharts'
import theme from '../../../styles/theme'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { getPerformanceStatsColumnTranslationKey } from '../../../utils/hooks/performanceStats/useStatsColumns'
import { DefaultTimeseriesStatStyle, getUnit, TimeseriesStatStyle, valueFormatter } from '../statsConf'
import { ChartOptionsType } from '../../../utils/hooks/performanceStats/usePerformanceStats'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezonePlugin from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezonePlugin)

const MaxPointsToShowAllTicks = 24

export const formatTimeseriesChartDate = (
  date: Dayjs | Date | string,
  timezoneName: string,
  chartDateFormat: string
): string => dayjs(date).tz(timezoneName).format(chartDateFormat)

export const getTimeseriesXAxisInterval = (pointCount: number): 0 | 'preserveStartEnd' =>
  pointCount <= MaxPointsToShowAllTicks ? 0 : 'preserveStartEnd'

interface TimeseriesChartProps<T extends { date: Dayjs }> {
  data: T[]
  timezone: string
  chartItemsVisibility: Record<keyof T, boolean>
  statStyles: Record<keyof T, TimeseriesStatStyle>
  chartDateFormat: string
  translationPrefix: string
  translateValues: boolean
  chartOptions: ChartOptionsType
  height?: number | `${number}%`
}

const BarTransparency = 'AA'

const timeseriesChart = <T extends { date: Dayjs }>({
  data,
  timezone,
  chartItemsVisibility,
  statStyles,
  chartDateFormat,
  translationPrefix,
  translateValues = true,
  chartOptions,
}: TimeseriesChartProps<T>): JSX.Element => {
  const t = useAppTranslations()
  const { displayValues } = chartOptions

  const dataForGraph = useMemo(
    () =>
      data.map(({ date, ...other }) =>
        Object.assign({}, other, {
          date: formatTimeseriesChartDate(date, timezone, chartDateFormat),
        })
      ),
    [data, timezone, chartDateFormat]
  )

  const [xAxises, yAxises, gridYAxisId] = useMemo(() => {
    const statsToDisplay = Object.entries(chartItemsVisibility).flatMap(([name, isVisible]) => {
      if (!isVisible) return []

      const statStyle: TimeseriesStatStyle = statStyles[name as keyof T] ?? DefaultTimeseriesStatStyle
      return { name, statStyle }
    })

    const xAxises = statsToDisplay
      .sort(({ statStyle: a }, { statStyle: b }) => {
        if (a.display === 'bar' && b.display !== 'bar') return -1
        if (a.display !== 'bar' && b.display === 'bar') return 1
        return 0
      })
      .map(({ name, statStyle }, i) => {
        if (statStyle.display === 'bar')
          return (
            <Bar
              key={i}
              dataKey={name.toString()}
              yAxisId={statStyle.family}
              stackId={statStyle.stackId}
              fill={`${statStyle.color}${BarTransparency}`}
              name={!translateValues ? statStyle.name : t(getPerformanceStatsColumnTranslationKey(statStyle.name))}
            >
              {displayValues && (
                <LabelList dataKey={name.toString()} position={'top'} fill={statStyle.color} offset={15} />
              )}
            </Bar>
          )

        return (
          <Line
            key={i}
            strokeWidth={2}
            type="monotone"
            dataKey={name.toString()}
            yAxisId={statStyle.family}
            stroke={statStyle.color}
            name={!translateValues ? statStyle.name : t(getPerformanceStatsColumnTranslationKey(statStyle.name))}
          >
            {displayValues && (
              <LabelList dataKey={name.toString()} position={'top'} fill={statStyle.color} offset={15} />
            )}
          </Line>
        )
      })

    const styleAndOrientations = Object.entries(
      groupBy(
        statsToDisplay.map(({ statStyle }) => statStyle),
        'family'
      )
    )
      .map(([_, statStyles]) =>
        statStyles.reduce((previous, statStyle) =>
          Object.assign({}, previous, {
            isAxisVisible: previous.isAxisVisible ?? (true && statStyle.isAxisVisible) ?? true,
          })
        )
      )
      .map((statStyle, index, all) => ({
        statStyle,
        orientation: (index < Math.round(all.length / 2) ? 'left' : 'right') as 'left' | 'right',
      }))

    const styleByOrientation = groupBy(styleAndOrientations, 'orientation')
    const sortedAxises = [...(styleByOrientation['left'] ?? []).reverse(), ...(styleByOrientation['right'] ?? [])]

    const yAxises = sortedAxises.map(({ statStyle, orientation }, index) => (
      <YAxis
        hide={statStyle.isAxisVisible === false}
        width={statStyle.isAxisVisible === false ? 0 : 60}
        domain={statStyle.family === 'percentage' ? [0, 100] : [0, 'auto']}
        stroke={theme.palette.grey[300]}
        key={index}
        interval="preserveStartEnd"
        yAxisId={statStyle.family}
        orientation={orientation}
        tickLine={false}
        tickMargin={0}
        axisLine={false}
        tick={{
          stroke: theme.palette.grey[500],
          fontSize: 11,
          fontWeight: 0,
        }}
        unit={getUnit(statStyle)}
        tickFormatter={(tick) => {
          if (tick >= 1000 && tick < 1000000) return tick / 1000 + 'K'
          else if (tick >= 1000000) return tick / 1000000 + 'M'
          else return tick
        }}
      >
        <Label
          value={t(`${translationPrefix}.${statStyle.family}`) as string}
          angle={-90}
          position={orientation === 'left' ? `insideLeft` : `insideRight`}
          fill={theme.palette.grey[500]}
          fontSize={12}
        />
      </YAxis>
    ))

    const gridYAxisId = sortedAxises[0]?.statStyle.family

    return [xAxises, yAxises, gridYAxisId]
  }, [chartItemsVisibility, statStyles, translationPrefix, displayValues])

  const tooltipFormatter = (value: number, name: string, row: any): string => {
    const style = statStyles[row.dataKey as keyof T]

    if (style === undefined) return value.toString()
    return valueFormatter(style, value)
  }

  const tooltipItemOrder = Object.keys(chartItemsVisibility)

  return (
    <ResponsiveContainer width="100%" height={350}>
      <ComposedChart data={dataForGraph} barSize={60} margin={{ top: 16, right: 24, left: 0, bottom: 8 }}>
        {gridYAxisId != null && <CartesianGrid strokeDasharray="6 6" vertical={false} yAxisId={gridYAxisId} />}

        {yAxises}

        <XAxis
          dataKey="date"
          type="category"
          interval={getTimeseriesXAxisInterval(dataForGraph.length)}
          minTickGap={dataForGraph.length <= MaxPointsToShowAllTicks ? 0 : 50}
          padding={{ left: 24, right: 24 }}
          stroke={theme.palette.grey[300]}
          tick={{
            stroke: theme.palette.grey[500],
            fontSize: 11,
          }}
        />

        <Tooltip
          formatter={tooltipFormatter}
          itemSorter={(item) => tooltipItemOrder.indexOf(item.dataKey?.toString() ?? '')}
        />

        {xAxises}
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export default timeseriesChart
